import api from '@/lib/axios';
import { USER_STORAGE_KEY, useUserStore } from '@/store/useUserStore';
import { getStoredAuthToken } from '@/lib/authStorage';
import {
  getAttributionChannelSource,
  getAttributionHeaders,
  getAttributionPayload,
} from '@/lib/utm';

const UUID_KEY = 'track_uuid';
const UUID_QUERY_KEYS = ['uuid', 'uu_id', 'track_uuid', 'trackUuid'] as const;
const TRACK_ENDPOINT = '/lowcode-business/api/user/event/track';

const normalizeId = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined;
  const text = String(value).trim();
  return text || undefined;
};

const getCookieValue = (name: string): string | undefined => {
  const parts = document.cookie ? document.cookie.split(';') : [];
  for (const part of parts) {
    const text = part.trim();
    if (text.startsWith(`${name}=`)) {
      return normalizeId(decodeURIComponent(text.slice(name.length + 1)));
    }
  }
  return undefined;
};

const getCookieDomain = (): string => {
  const hostname = window.location.hostname;
  if (
    !hostname ||
    hostname === 'localhost' ||
    /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname) ||
    hostname.includes(':')
  ) {
    return '';
  }

  const parts = hostname.split('.');
  return parts.length >= 2 ? `.${parts.slice(-2).join('.')}` : '';
};

const setSharedCookie = (name: string, value: string, maxAge: number): void => {
  if (getCookieValue(name) === value) return;

  const cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
  document.cookie = cookie;
  const domain = getCookieDomain();
  if (domain) {
    document.cookie = `${cookie}; domain=${domain}`;
  }
};

const createUuid = (): string => {
  if (crypto.randomUUID) return crypto.randomUUID();

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    const value = char === 'x' ? random : (random % 4) + 8;
    return value.toString(16);
  });
};

const getCombinedTrackParams = (): URLSearchParams => {
  const params = new URLSearchParams(window.location.search);
  const hashQuery = window.location.hash.split('?')[1] || '';
  if (hashQuery) {
    new URLSearchParams(hashQuery).forEach((value, key) => {
      if (!params.has(key)) params.set(key, value);
    });
  }
  return params;
};

const getUuidFromUrl = (): string | undefined => {
  const params = getCombinedTrackParams();

  for (const key of UUID_QUERY_KEYS) {
    const value = normalizeId(params.get(key));
    if (value) return value;
  }

  return undefined;
};

function getOrCreateUuid(): string {
  const uuid =
    getUuidFromUrl() ??
    normalizeId(localStorage.getItem(UUID_KEY)) ??
    getCookieValue(UUID_KEY) ??
    createUuid();

  if (localStorage.getItem(UUID_KEY) !== uuid) {
    localStorage.setItem(UUID_KEY, uuid);
  }
  setSharedCookie(UUID_KEY, uuid, 31536000);
  return uuid;
}

export interface TrackEventParams {
  eventCode: string;
  eventData?: string;
  userId?: string;
  channelSource?: string;
  transport?: 'xhr' | 'beacon';
}

interface TrackPayload {
  eventCode: string;
  uuid: string;
  userId?: string;
  eventData?: string;
  url: string;
  referrer?: string;
  channelSource?: string;
  [key: string]: string | undefined;
}

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const getPersistedUserId = (): string | undefined => {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    if (!raw) return undefined;

    const parsed = JSON.parse(raw) as { state?: { user?: { id?: unknown } } };
    return normalizeId(parsed.state?.user?.id);
  } catch {
    return undefined;
  }
};

const decodeBase64Url = (value: string): string => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

const getTokenUserId = (): string | undefined => {
  try {
    const token = getStoredAuthToken();
    const payload = token?.split('.')[1];
    if (!payload) return undefined;

    const parsed = JSON.parse(decodeBase64Url(payload)) as Record<string, unknown>;
    return normalizeId(parsed.userId ?? parsed.user_id ?? parsed.uid ?? parsed.id ?? parsed.sub);
  } catch {
    return undefined;
  }
};

const resolveTrackUserId = (userId?: string): string | undefined =>
  normalizeId(userId) ??
  normalizeId(useUserStore.getState().user?.id) ??
  getPersistedUserId() ??
  getTokenUserId();

export const resolveTrackChannelSource = (channelSource?: string): string | undefined =>
  normalizeId(channelSource) ??
  getAttributionChannelSource() ??
  normalizeId(import.meta.env.VITE_CHANNEL_SOURCE);

export const mergeAttributionIntoEventData = (
  eventData: string | undefined,
  attributionPayload: Record<string, string>,
): string | undefined => {
  if (Object.keys(attributionPayload).length === 0) return eventData;

  if (!eventData) {
    return JSON.stringify(attributionPayload);
  }

  try {
    const parsed = JSON.parse(eventData);
    if (isPlainRecord(parsed)) {
      return JSON.stringify({
        ...attributionPayload,
        ...parsed,
      });
    }
  } catch {
    // Preserve non-JSON event data while still attaching attribution fields.
  }

  return JSON.stringify({
    ...attributionPayload,
    eventData,
  });
};

const buildTrackPayload = (params: TrackEventParams): TrackPayload => {
  const { eventCode, eventData } = params;
  const channelSource = resolveTrackChannelSource(params.channelSource);
  const attributionPayload = getAttributionPayload(channelSource);

  return {
    eventCode,
    uuid: getOrCreateUuid(),
    userId: resolveTrackUserId(params.userId),
    eventData: mergeAttributionIntoEventData(eventData, attributionPayload),
    url: window.location.href,
    referrer: document.referrer || undefined,
    ...attributionPayload,
    channelSource,
  };
};

const getBeaconEndpoint = () => {
  const baseUrl = import.meta.env.DEV ? '' : String(import.meta.env.VITE_API_BASE_URL || '');
  if (!baseUrl) return TRACK_ENDPOINT;
  return `${baseUrl.replace(/\/$/, '')}${TRACK_ENDPOINT}`;
};

const sendBeacon = (payload: TrackPayload): boolean => {
  if (!navigator.sendBeacon) return false;

  const body = new Blob([JSON.stringify(payload)], { type: 'application/json' });
  return navigator.sendBeacon(getBeaconEndpoint(), body);
};

const buildTrackHeaders = (payload: TrackPayload): Record<string, string> => {
  const headers = getAttributionHeaders(payload.channelSource);
  if (payload.channelSource) {
    headers['Channel-Source'] = payload.channelSource;
  }
  return headers;
};

const sendKeepalive = (payload: TrackPayload, headers: Record<string, string>): boolean => {
  if (!window.fetch) return false;

  try {
    const token = getStoredAuthToken();
    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...headers,
    };

    if (token) {
      requestHeaders.Authorization = `Bearer ${token}`;
      requestHeaders.token = token;
    }

    void fetch(getBeaconEndpoint(), {
      method: 'POST',
      headers: requestHeaders,
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: 'same-origin',
    }).catch(() => {
      // Tracking failures should not block product flows.
    });
    return true;
  } catch {
    return false;
  }
};

export function trackEvent(params: TrackEventParams): void {
  const payload = buildTrackPayload(params);
  const headers = buildTrackHeaders(payload);
  const hasHeaders = Object.keys(headers).length > 0;

  if (params.transport === 'beacon' && sendKeepalive(payload, headers)) return;
  if (params.transport === 'beacon' && !hasHeaders && sendBeacon(payload)) return;

  api
    .post(TRACK_ENDPOINT, payload, {
      headers: hasHeaders ? headers : undefined,
      _skipAuthCheck: true,
      _suppressErrorToast: true,
    })
    .catch(() => {
      // Tracking failures should not block product flows.
    });
}
